/**
 * AI Recruit - Source Management Page
 * Admin page for managing job and resume sources
 */

import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { 
  PlusIcon, 
  PencilIcon, 
  CheckCircleIcon,
  XCircleIcon,
  TagIcon
} from '@heroicons/react/24/outline'

import LoadingSpinner from '@/components/common/LoadingSpinner'
import { fetchSources, createSource, updateSource, activateSource, deactivateSource, type Source } from '@/services/sourceApi'
import { usePermission } from '@/hooks/usePermission'

type TabType = 'job' | 'resume'

const SourceManagementPage: React.FC = () => {
  const { can } = usePermission()
  const [activeTab, setActiveTab] = useState<TabType>('job')
  // Gate mismatch prevention — this page previously had zero permission
  // logic of its own (access was all-or-nothing at the route level, gated
  // on sources.manageJobSources/manageResumeSources). Now that a new
  // sources.viewJobSources/viewResumeSources pair (granted to all roles)
  // makes the route reachable by everyone, the create/edit/activate/
  // deactivate actions below need their own explicit gate so a view-only
  // role doesn't see fully-functional-looking buttons that would 403.
  //
  // Split 2026-08-25 (§27) from one combined `canManage` flag into three,
  // matching sources.manageJobSources/manageResumeSources's own further
  // split into create/edit/activate-deactivate — each currently granted to
  // the exact same roles (Admin/Manager/Super Admin) as the bundled key it
  // replaced, so no access change, just finer-grained checks.
  const canCreateSource = activeTab === 'job' ? can('sources.createJobSource') : can('sources.createResumeSource')
  const canEditSource = activeTab === 'job' ? can('sources.editJobSource') : can('sources.editResumeSource')
  const canToggleSourceStatus = activeTab === 'job' ? can('sources.activateDeactivateJobSource') : can('sources.activateDeactivateResumeSource')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingSource, setEditingSource] = useState<Source | null>(null)
  const [sourceName, setSourceName] = useState('')
  const [error, setError] = useState('')

  const queryClient = useQueryClient()

  // Fetch sources
  const { data: sources, isLoading } = useQuery({
    queryKey: ['sources', activeTab],
    queryFn: () => fetchSources(activeTab, true), // Include inactive sources
  })

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: { name: string; type: TabType }) => createSource(data.type, data.name),
    onSuccess: (_data, variables) => {
      const label = variables.type === 'job' ? 'Job' : 'Resume'
      toast.success(`${label} Source created successfully!`)
      queryClient.invalidateQueries({ queryKey: ['sources', activeTab] })
      setShowCreateModal(false)
      setSourceName('')
      setError('')
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Failed to create source'
      setError(message)
      toast.error(message)
    }
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: { id: string; name: string; type: TabType }) => 
      updateSource(data.type, data.id, data.name),
    onSuccess: (_data, variables) => {
      const label = variables.type === 'job' ? 'Job' : 'Resume'
      toast.success(`${label} Source updated successfully!`)
      queryClient.invalidateQueries({ queryKey: ['sources', activeTab] })
      setShowEditModal(false)
      setEditingSource(null)
      setSourceName('')
      setError('')
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Failed to update source'
      setError(message)
      toast.error(message)
    }
  })

  // Activate mutation
  const activateMutation = useMutation({
    mutationFn: (data: { id: string; type: TabType }) => activateSource(data.type, data.id),
    onSuccess: (_data, variables) => {
      const label = variables.type === 'job' ? 'Job' : 'Resume'
      toast.success(`${label} Source activated successfully!`)
      queryClient.invalidateQueries({ queryKey: ['sources', activeTab] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to activate source')
    }
  })

  // Deactivate mutation
  const deactivateMutation = useMutation({
    mutationFn: (data: { id: string; type: TabType }) => deactivateSource(data.type, data.id),
    onSuccess: (_data, variables) => {
      const label = variables.type === 'job' ? 'Job' : 'Resume'
      toast.success(`${label} Source deactivated successfully!`)
      queryClient.invalidateQueries({ queryKey: ['sources', activeTab] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to deactivate source')
    }
  })

  const handleCreate = () => {
    if (!sourceName.trim()) {
      setError('Source name is required')
      return
    }
    createMutation.mutate({ name: sourceName.trim(), type: activeTab })
  }

  const handleEdit = () => {
    if (!sourceName.trim() || !editingSource) {
      setError('Source name is required')
      return
    }
    updateMutation.mutate({ id: editingSource.id, name: sourceName.trim(), type: activeTab })
  }

  const handleToggleStatus = (source: Source) => {
    if (source.is_active) {
      if (confirm(`Are you sure you want to deactivate "${source.name}"? It will no longer appear in dropdowns.`)) {
        deactivateMutation.mutate({ id: source.id, type: activeTab })
      }
    } else {
      activateMutation.mutate({ id: source.id, type: activeTab })
    }
  }

  const openEditModal = (source: Source) => {
    setEditingSource(source)
    setSourceName(source.name)
    setError('')
    setShowEditModal(true)
  }

  const openCreateModal = () => {
    setSourceName('')
    setError('')
    setShowCreateModal(true)
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Manage Sources</h1>
        <p className="mt-1 text-sm text-gray-600">
          Create and manage sources for tracking jobs and resumes
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('job')}
            className={`${
              activeTab === 'job'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Job Sources
          </button>
          <button
            onClick={() => setActiveTab('resume')}
            className={`${
              activeTab === 'resume'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Resume Sources
          </button>
        </nav>
      </div>

      {/* Create button */}
      {canCreateSource && (
        <div className="mb-4">
          <button
            onClick={openCreateModal}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            New {activeTab === 'job' ? 'Job' : 'Resume'} Source
          </button>
        </div>
      )}

      {/* Sources list */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : sources && sources.length > 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {sources.map((source) => (
              <li key={source.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center">
                  <TagIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className={`text-sm font-medium ${source.is_active ? 'text-gray-900' : 'text-gray-400'}`}>
                      {source.name}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {source.is_active ? (
                        <span className="inline-flex items-center text-green-700">
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-gray-500">
                          <XCircleIcon className="h-4 w-4 mr-1" />
                          Inactive
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                {(canEditSource || canToggleSourceStatus) && (
                  <div className="flex items-center space-x-2">
                    {canEditSource && (
                      <button
                        onClick={() => openEditModal(source)}
                        disabled={!source.is_active}
                        className={`p-2 ${
                          source.is_active
                            ? 'text-gray-400 hover:text-gray-600'
                            : 'text-gray-300 cursor-not-allowed'
                        }`}
                        title={source.is_active ? "Edit" : "Cannot edit inactive source"}
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                    )}
                    {canToggleSourceStatus && (
                      <button
                        onClick={() => handleToggleStatus(source)}
                        className={`px-3 py-1 text-sm rounded ${
                          source.is_active
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {source.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
          <TagIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No sources</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating a new {activeTab} source.
          </p>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowCreateModal(false)} />
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Create New {activeTab === 'job' ? 'Job' : 'Resume'} Source
                </h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Source Name *
                  </label>
                  <input
                    type="text"
                    value={sourceName}
                    onChange={(e) => setSourceName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter source name"
                  />
                  {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  onClick={handleCreate}
                  disabled={createMutation.isPending}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:col-start-2 sm:text-sm disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create'}
                </button>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingSource && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowEditModal(false)} />
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Edit Source
                </h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Source Name *
                  </label>
                  <input
                    type="text"
                    value={sourceName}
                    onChange={(e) => setSourceName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter source name"
                  />
                  {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  onClick={handleEdit}
                  disabled={updateMutation.isPending}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:col-start-2 sm:text-sm disabled:opacity-50"
                >
                  {updateMutation.isPending ? 'Updating...' : 'Update'}
                </button>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SourceManagementPage
